import type { TFunction } from "i18next";
import { precipClass } from "@divespots/contracts";
import type { Locale } from "@divespots/contracts";
import { formatNumber } from "../../i18n/format.ts";
import { hourParts } from "../precip/time.ts";
import type { PrecipReading } from "../precip/readout.ts";
import type { FrameState } from "../radar/frames.ts";
import type { RadarReading } from "../radar/raster.ts";
import type { WindReading } from "./readout.ts";

export interface WeatherLineInput {
  locale: Locale;
  now: number;
  windOn: boolean;
  /** A measured radar step is selected: the precipitation part comes from the radar picture. */
  atRadar: boolean;
  /** Wind and precipitation share the bar: the forecast hour is named in the precipitation part. */
  both: boolean;
  /** Radar step before the wind field's first time. */
  windMissing: boolean;
  wind: { place: string; wind: WindReading | null } | null;
  precip?:
    | {
        status: "loading" | "ready" | "unavailable";
        readout: { place: string; reading: PrecipReading | null } | null;
      }
    | undefined;
  radar?:
    | {
        frame: FrameState | undefined;
        readout: { place: string; reading: RadarReading | null } | null;
      }
    | undefined;
}

const SEP = " · ";

/** "mässig" for a value; the class names live in the legend. */
function intensity(t: TFunction, mm: number, capped: boolean, locale: Locale): string {
  const cls = Math.max(0, precipClass(mm));
  return t("weather.precip", {
    mm: `${capped ? "≥ " : ""}${formatNumber(mm, locale, capped ? 0 : 1)}`,
    cls: t(`precip.class${cls}`),
  });
}

/**
 * The one readout line of the bar: the place once, then the wind and the precipitation of that
 * place ("Kartenmitte · 5 km/h aus W · kein Niederschlag"). Null when nothing is known yet.
 */
export function weatherLine(t: TFunction, i: WeatherLineInput): string | null {
  const place = i.wind?.place ?? i.precip?.readout?.place ?? i.radar?.readout?.place ?? null;
  const parts: string[] = [];

  if (i.windOn && i.wind) {
    const w = i.wind.wind;
    if (i.windMissing && i.atRadar) parts.push(t("weather.noWind"));
    else if (w === null) parts.push(t("weather.windNone"));
    else {
      const text = w.calm
        ? t("weather.calm")
        : t("weather.wind", { kmh: w.kmh, dir: t(`wind.dir.${w.from}`) });
      // at a measured radar step the wind is still the model's: say so
      parts.push(i.atRadar ? `${text} ${t("radar.windModel")}` : text);
    }
  }

  const precip = (() => {
    if (!i.precip) return null;
    if (i.atRadar && i.radar) {
      const r = i.radar.readout;
      if (i.radar.frame?.status === "error") return t("weather.radarNone");
      if (!r) return null;
      if (!r.reading) return t("radar.loading");
      if (r.reading.nodata) return t("weather.radarNone");
      if (r.reading.dry) return t("weather.dry");
      return intensity(t, r.reading.mm, r.reading.capped, i.locale);
    }
    if (i.precip.status !== "ready")
      return t(i.precip.status === "loading" ? "precip.loading" : "precip.unavailable");
    const r = i.precip.readout;
    if (!r) return null;
    if (!r.reading) return t("weather.precipNone");
    let text = r.reading.dry ? t("weather.dry") : intensity(t, r.reading.mm, false, i.locale);
    if (i.both) {
      const p = hourParts(r.reading.fromMs, i.now, i.locale);
      text = `${p.day ? `${p.day} ` : ""}${t("precip.hours", { from: p.from, to: p.to })} ${text}`;
    }
    return text;
  })();
  if (precip !== null) parts.push(precip);

  if (parts.length === 0) return null;
  return [place ?? t("wind.placeCenter"), ...parts].join(SEP);
}
